import { useCallback, useEffect, useRef, useState } from "react";

import { galleryImagePath } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";
import { Modal } from "../components/Modal";

import { formatDateTime } from "./admin/shared";
import {
  POST_STATUS_LABEL,
  REPORT_REASON_LABEL,
  reactionTotal,
} from "./gallery/reactions";

import type { ApiErrorCode } from "../api/client";
import type { AdminGalleryPost } from "@canvaschool/contracts";

type Filter = "REPORTED" | "HIDDEN" | "ALL";

const TABS: Array<{ filter: Filter; label: string }> = [
  { filter: "REPORTED", label: "신고됨" },
  { filter: "HIDDEN", label: "숨김" },
  { filter: "ALL", label: "전체" },
];

const REASONS = ["PERSONAL_INFO", "INAPPROPRIATE", "OTHER"] as const;

/** Administrators decide posts that reports hid, and may delete any post. */
export const AdminGalleryPage = () => {
  const auth = useAuth();
  const { api } = auth;
  const [filter, setFilter] = useState<Filter>("REPORTED");
  const [items, setItems] = useState<AdminGalleryPost[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AdminGalleryPost | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  /** Only the newest request may write the list. */
  const requestRef = useRef(0);
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const load = useCallback(
    async (cursor?: string) => {
      const ticket = ++requestRef.current;
      try {
        const result = await api.listAdminGalleryPosts({
          filter: filterRef.current,
          cursor,
        });
        if (ticket === requestRef.current) {
          setItems((current) =>
            cursor ? [...(current ?? []), ...result.items] : result.items,
          );
          setNextCursor(result.nextCursor);
        }
      } catch (failure) {
        if (ticket === requestRef.current) {
          setError(auth.handleError(failure));
        }
      }
    },
    [api, auth],
  );

  useEffect(() => {
    setError(null);
    setItems(null);
    setNextCursor(null);
    void load();
    // Reloading per filter; `auth` changes identity on every session update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, filter]);

  /** One decision at a time, so a double click never repeats it. */
  const run = async (action: () => Promise<unknown>) => {
    if (busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await action();
      setConfirmDelete(null);
      await load();
    } catch (failure) {
      setError(auth.handleError(failure));
      setConfirmDelete(null);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const loadMore = async () => {
    if (!nextCursor || busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    try {
      await load(nextCursor);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <section className="canvaschool-page">
      <div className="canvaschool-title-row">
        <div>
          <h1>갤러리 관리</h1>
          <p className="canvaschool-page-lead">
            신고가 3명 모이거나 선생님이 신고한 작품은 숨겨집니다.
          </p>
        </div>
      </div>
      <div className="canvaschool-tabs" role="tablist" aria-label="보기">
        {TABS.map((tab) => (
          <button
            key={tab.filter}
            type="button"
            role="tab"
            aria-selected={filter === tab.filter}
            className="canvaschool-tab"
            onClick={() => setFilter(tab.filter)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {error && <ErrorAlert code={error} />}
      {items === null && !error && <p aria-busy="true">불러오는 중…</p>}
      {items && items.length === 0 && (
        <p className="canvaschool-hint">조건에 맞는 작품이 없습니다.</p>
      )}
      {items && items.length > 0 && (
        <div className="canvaschool-table-wrap">
          <table className="canvaschool-table">
            <thead>
              <tr>
                <th scope="col">작품</th>
                <th scope="col">학교 · 학급</th>
                <th scope="col">담임 아이디</th>
                <th scope="col">상태</th>
                <th scope="col">신고</th>
                <th scope="col">반응</th>
                <th scope="col">올린 날</th>
                <th scope="col">관리</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <img
                      className="canvaschool-gallery-thumb"
                      src={galleryImagePath(item.id)}
                      alt={`${item.school.name} ${item.className} 작품`}
                      loading="lazy"
                    />
                  </td>
                  <td>
                    {item.school.name} · {item.className}
                  </td>
                  <td>
                    <span className="canvaschool-prefix">
                      {item.teacherLoginId}
                    </span>
                  </td>
                  <td>
                    <span
                      className={
                        item.status === "PUBLISHED"
                          ? "canvaschool-status canvaschool-status--on"
                          : item.status === "HIDDEN"
                          ? "canvaschool-status canvaschool-status--wait"
                          : item.status === "DELETED"
                          ? "canvaschool-status canvaschool-status--bad"
                          : "canvaschool-status"
                      }
                    >
                      {POST_STATUS_LABEL[item.status]}
                    </span>
                  </td>
                  <td>
                    {REASONS.map(
                      (reason) =>
                        `${REPORT_REASON_LABEL[reason]} ${item.reports[reason]}`,
                    ).join(" · ")}
                  </td>
                  <td>{reactionTotal(item.reactions)}</td>
                  <td>{formatDateTime(item.createdAt)}</td>
                  <td>
                    <div className="canvaschool-actions">
                      {item.status === "HIDDEN" && (
                        <button
                          type="button"
                          className="canvaschool-btn-primary"
                          aria-label={`${item.school.name} ${item.className} 작품 다시 보이기`}
                          disabled={busy}
                          onClick={() =>
                            void run(() => api.restoreGalleryPost(item.id))
                          }
                        >
                          다시 보이기
                        </button>
                      )}
                      {item.status !== "DELETED" && (
                        <button
                          type="button"
                          className="canvaschool-btn-danger"
                          aria-label={`${item.school.name} ${item.className} 작품 삭제`}
                          disabled={busy}
                          onClick={() => setConfirmDelete(item)}
                        >
                          삭제
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {nextCursor && (
        <button type="button" onClick={() => void loadMore()} disabled={busy}>
          더 보기
        </button>
      )}
      {confirmDelete && (
        <Modal
          title="작품 삭제"
          primaryLabel="삭제"
          danger
          pending={busy}
          onCancel={() => setConfirmDelete(null)}
          onPrimary={() =>
            void run(() => api.deleteGalleryPost(confirmDelete.id))
          }
        >
          <div className="canvaschool-modal-danger-box">
            <p>
              <strong>
                {confirmDelete.school.name} · {confirmDelete.className}
              </strong>
              의 작품을 삭제합니다. 되돌릴 수 없고, 학생은 이 프레임을 다시 올릴
              수 없습니다.
            </p>
          </div>
        </Modal>
      )}
    </section>
  );
};
